class ScAdditionalProducts extends HTMLElement {
  constructor() {
    super();

    this.abortController = null;
    this.form = null;
    this.fieldsContainer = null;
    this.cards = [];
    this.selectedItems = new Map();
    this.submitButtons = [];
    this.isSubmitting = false;

    this.onCardClick = this.onCardClick.bind(this);
    this.onCardKeydown = this.onCardKeydown.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onSubmitButtonClick = this.onSubmitButtonClick.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onProductRerender = this.onProductRerender.bind(this);
  }

  connectedCallback() {
    this.abortController = new AbortController();
    this.form = document.forms[this.dataset.formId];
    this.fieldsContainer = this.form?.querySelector("[data-additional-products-fields]");

    if (!this.form || !this.fieldsContainer) {
      return;
    }

    this.cards = Array.from(
      this.querySelectorAll(".sc-additional_products-product_card")
    );

    this.cards.forEach((card) => {
      card.addEventListener("click", this.onCardClick, {
        signal: this.abortController.signal,
      });
      card.addEventListener("keydown", this.onCardKeydown, {
        signal: this.abortController.signal,
      });
    });

    this.form.addEventListener("submit", this.onSubmit, {
      capture: true,
      signal: this.abortController.signal,
    });
    this.form.addEventListener("variant:change", this.onVariantChange, {
      signal: this.abortController.signal,
    });
    this.form.addEventListener("product:rerender", this.onProductRerender, {
      signal: this.abortController.signal,
    });

    this.syncSelectedItemsFromDom();
    this.refreshSubmitButtons();
    this.syncAdditionalItemFields();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    this.form = null;
    this.fieldsContainer = null;
    this.cards = [];
    this.submitButtons = [];
    this.selectedItems.clear();
    this.isSubmitting = false;
  }

  onCardClick(event) {
    const card = event.currentTarget;

    this.toggleCard(card);
  }

  onCardKeydown(event) {
    if (event.key !== " " && event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    this.toggleCard(event.currentTarget);
  }

  async onSubmitButtonClick(event) {
    if (!this.hasSelectedItems()) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.isSubmitting) {
      return;
    }

    if (!this.form?.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    await this.submitSelectedItems();
  }

  onVariantChange() {
    this.syncAdditionalItemFields();
  }

  onProductRerender() {
    window.requestAnimationFrame(() => {
      this.refreshSubmitButtons();
      this.syncAdditionalItemFields();
    });
  }

  toggleCard(card) {
    const isActive = card.dataset.active !== "true";

    card.dataset.active = String(isActive);
    card.dataset.selected = String(isActive);
    card.setAttribute("aria-pressed", String(isActive));

    this.updateSelectedItem(card);
    this.syncAdditionalItemFields();
  }

  syncSelectedItemsFromDom() {
    this.selectedItems.clear();
    this.cards.forEach((card) => this.updateSelectedItem(card));
  }

  updateSelectedItem(card) {
    const variantId = this.parseNumber(card.dataset.variantId);

    if (!variantId) {
      return;
    }

    if (card.dataset.active !== "true") {
      this.selectedItems.delete(variantId);
      return;
    }

    const quantity = this.normalizePositiveInteger(card.dataset.quantity, 1);
    const sellingPlan = this.parseNumber(card.dataset.sellingPlan);
    const properties = this.parseProperties(card.dataset.properties);

    this.selectedItems.set(variantId, {
      id: variantId,
      quantity,
      parentMode: card.dataset.parentMode || "independent",
      sellingPlan,
      properties,
    });
  }

  refreshSubmitButtons() {
    this.submitButtons = this.getSubmitButtons();

    this.submitButtons.forEach((button) => {
      button.addEventListener("click", this.onSubmitButtonClick, {
        signal: this.abortController.signal,
      });
    });
  }

  getSubmitButtons() {
    const formId = this.form?.getAttribute("id");

    if (!formId) {
      return [];
    }

    return Array.from(
      document.querySelectorAll(
        `buy-buttons[form="${formId}"] button[type="submit"], buy-buttons[form="${formId}"] input[type="submit"]`
      )
    ).filter((button) => !button.closest(".shopify-payment-button"));
  }

  shouldInterceptSubmit(event) {
    if (!this.hasSelectedItems()) {
      return false;
    }

    if (!event.submitter) {
      return true;
    }

    if (event.submitter.closest(".shopify-payment-button")) {
      return false;
    }

    return this.submitButtons.includes(event.submitter);
  }

  async onSubmit(event) {
    if (!this.shouldInterceptSubmit(event)) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.isSubmitting) {
      return;
    }

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    await this.submitSelectedItems();
  }

  async submitSelectedItems() {
    const primaryItem = this.buildPrimaryItem();

    if (!primaryItem) {
      return;
    }

    const submitButtons = this.getSubmitButtons();

    submitButtons.forEach((button) => {
      button.setAttribute("aria-busy", "true");
    });

    let sectionsToBundle = ["variant-added"];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const payload = {
      items: [primaryItem, ...this.getSelectedItems().map((item) => this.buildAdditionalItem(item))],
      sections: sectionsToBundle.join(","),
      sections_url: `${Shopify.routes.root}variants/${primaryItem.id}`,
    };

    this.isSubmitting = true;

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(payload),
      });
      const responseJson = await response.json();

      if (response.ok) {
        if (
          window.themeVariables.settings.cartType === "page" ||
          window.themeVariables.settings.pageType === "cart"
        ) {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent.sections = responseJson.sections;

        this.form.dispatchEvent(
          new CustomEvent("variant:add", {
            bubbles: true,
            detail: {
              items: responseJson.hasOwnProperty("items")
                ? responseJson.items
                : [responseJson],
              cart: cartContent,
            },
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: {
              baseEvent: "variant:add",
              cart: cartContent,
            },
          })
        );
      } else {
        this.form.dispatchEvent(
          new CustomEvent("cart:error", {
            bubbles: true,
            detail: {
              error: responseJson.description,
            },
          })
        );
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      }
    } catch (error) {
      this.form.dispatchEvent(
        new CustomEvent("cart:error", {
          bubbles: true,
          detail: {
            error: error?.message || "Unable to add items to cart.",
          },
        })
      );
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    } finally {
      submitButtons.forEach((button) => {
        button.removeAttribute("aria-busy");
      });

      this.isSubmitting = false;
    }
  }

  buildPrimaryItem() {
    if (!this.form) {
      return null;
    }

    const formData = new FormData(this.form);
    const id = this.parseNumber(formData.get("id"));

    if (!id) {
      return null;
    }

    const primaryItem = {
      id,
      quantity: this.normalizePositiveInteger(formData.get("quantity"), 1),
    };
    const sellingPlan = this.parseNumber(formData.get("selling_plan"));
    const properties = this.extractPrimaryProperties(formData);

    if (sellingPlan) {
      primaryItem.selling_plan = sellingPlan;
    }

    if (Object.keys(properties).length > 0) {
      primaryItem.properties = properties;
    }

    return primaryItem;
  }

  buildAdditionalItem(item) {
    const additionalItem = {
      id: item.id,
      quantity: item.quantity,
    };

    if (item.parentMode === "main") {
      const primaryVariantId = this.getPrimaryVariantId();

      if (primaryVariantId) {
        additionalItem.parent_id = primaryVariantId;
      }
    }

    if (item.sellingPlan) {
      additionalItem.selling_plan = item.sellingPlan;
    }

    if (item.properties && Object.keys(item.properties).length > 0) {
      additionalItem.properties = item.properties;
    }

    return additionalItem;
  }

  extractPrimaryProperties(formData) {
    const properties = {};

    for (const [name, value] of formData.entries()) {
      const propertyMatch = name.match(/^properties\[(.+)\]$/);

      if (!propertyMatch) {
        continue;
      }

      if (value instanceof File) {
        if (!value.name) {
          continue;
        }

        properties[propertyMatch[1]] = value.name;
        continue;
      }

      properties[propertyMatch[1]] = value;
    }

    return properties;
  }

  syncAdditionalItemFields() {
    if (!this.fieldsContainer) {
      return;
    }

    this.fieldsContainer.replaceChildren();

    if (!this.hasSelectedItems()) {
      return;
    }

    this.getSelectedItems().forEach((item, index) => {
      const itemIndex = index + 1;

      this.fieldsContainer.appendChild(
        this.createHiddenInput(
          `shopify-additional-items[${itemIndex}][id]`,
          item.id
        )
      );
      this.fieldsContainer.appendChild(
        this.createHiddenInput(
          `shopify-additional-items[${itemIndex}][quantity]`,
          item.quantity
        )
      );

      if (item.parentMode === "main") {
        const primaryVariantId = this.getPrimaryVariantId();

        if (primaryVariantId) {
          this.fieldsContainer.appendChild(
            this.createHiddenInput(
              `shopify-additional-items[${itemIndex}][parent_id]`,
              primaryVariantId
            )
          );
        }
      }

      if (item.sellingPlan) {
        this.fieldsContainer.appendChild(
          this.createHiddenInput(
            `shopify-additional-items[${itemIndex}][selling_plan]`,
            item.sellingPlan
          )
        );
      }

      if (item.properties && Object.keys(item.properties).length > 0) {
        Object.entries(item.properties).forEach(([key, value]) => {
          this.fieldsContainer.appendChild(
            this.createHiddenInput(
              `shopify-additional-items[${itemIndex}][properties][${key}]`,
              value
            )
          );
        });
      }
    });
  }

  createHiddenInput(name, value) {
    const input = document.createElement("input");

    input.type = "hidden";
    input.name = name;
    input.value = `${value}`;

    return input;
  }

  getPrimaryVariantId() {
    return this.parseNumber(this.form?.elements?.id?.value);
  }

  hasSelectedItems() {
    return this.selectedItems.size > 0;
  }

  getSelectedItems() {
    return this.cards
      .filter((card) => card.dataset.active === "true")
      .map((card) => this.selectedItems.get(this.parseNumber(card.dataset.variantId)))
      .filter(Boolean);
  }

  normalizePositiveInteger(value, fallback) {
    const parsedValue = parseInt(value, 10);

    if (Number.isNaN(parsedValue) || parsedValue <= 0) {
      return fallback;
    }

    return parsedValue;
  }

  parseNumber(value) {
    const parsedValue = Number(value);

    if (!parsedValue) {
      return null;
    }

    return parsedValue;
  }

  parseProperties(value) {
    if (!value) {
      return {};
    }

    try {
      const parsedValue = JSON.parse(value);

      if (parsedValue && typeof parsedValue === "object") {
        return parsedValue;
      }
    } catch (error) {
      console.warn("Invalid additional product properties payload", error);
    }

    return {};
  }
}

if (!window.customElements.get("sc-additional-products")) {
  window.customElements.define("sc-additional-products", ScAdditionalProducts);
}
